import React, { useEffect, useState } from "react";
import { Globe, Smartphone, Laptop, Tablet, RefreshCw, Users, MapPin, Eye } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEVICE_ICON = { mobile: Smartphone, desktop: Laptop, tablet: Tablet };

function StatCard({ icon: Icon, label, value, accent = "blue" }) {
  const accents = {
    blue: "from-[#1d6cb1] to-[#2a83ce]",
    purple: "from-purple-600 to-fuchsia-600",
    emerald: "from-emerald-500 to-teal-600",
    amber: "from-amber-500 to-orange-500",
  };
  return (
    <div className="bg-white border border-blue-100 rounded-2xl p-5 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs uppercase tracking-wider text-gray-500">{label}</p>
        <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${accents[accent]} text-white flex items-center justify-center`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <p className="text-3xl font-extrabold text-[#0e4a7a]" style={{ fontFamily: "'Playfair Display', serif" }}>{value}</p>
    </div>
  );
}

function BarRow({ label, count, max }) {
  const pct = max > 0 ? Math.round((count / max) * 100) : 0;
  return (
    <div className="flex items-center gap-3 text-sm">
      <span className="w-32 truncate text-gray-700">{label}</span>
      <div className="flex-1 bg-blue-50 h-2.5 rounded-full overflow-hidden">
        <div className="h-full bg-gradient-to-r from-[#1d6cb1] to-[#2a83ce]" style={{ width: `${pct}%` }} />
      </div>
      <span className="w-12 text-right font-bold text-[#0e4a7a]">{count}</span>
    </div>
  );
}

export default function AdminVisits() {
  const [hours, setHours] = useState(24);
  const [stats, setStats] = useState(null);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({ country: "", device: "", path: "", only_logged_in: false });

  const load = async () => {
    setLoading(true);
    try {
      const [s, v] = await Promise.all([
        axios.get(`${API}/admin/visits/stats`, { params: { hours } }).then((r) => r.data),
        axios.get(`${API}/admin/visits`, { params: { ...filter, limit: 200 } }).then((r) => r.data),
      ]);
      setStats(s);
      setList(v);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [hours]); // eslint-disable-line

  const max = (arr) => Math.max(1, ...arr.map((x) => x.count));

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-3xl shadow-md border border-blue-100 p-6 sm:p-8">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
          <div>
            <h2 className="text-2xl font-extrabold text-[#0e4a7a]">Visitas a la página</h2>
            <p className="text-sm text-gray-500">Quién entró, desde dónde y con qué dispositivo (anónimos + registrados).</p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={hours}
              onChange={(e) => setHours(parseInt(e.target.value))}
              className="px-3 py-2 rounded-full border-2 border-blue-100 text-sm font-semibold text-[#1d6cb1] bg-white"
              data-testid="visits-hours-select"
            >
              <option value={1}>Última hora</option>
              <option value={6}>Últimas 6 horas</option>
              <option value={24}>Últimas 24 horas</option>
              <option value={72}>3 días</option>
              <option value={168}>7 días</option>
            </select>
            <button onClick={load} className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-blue-50 text-[#1d6cb1] font-semibold text-sm border border-blue-100 hover:bg-blue-100">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Actualizar
            </button>
          </div>
        </div>

        {stats && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <StatCard icon={Eye} label="Visitas totales" value={stats.total_visits} accent="blue" />
            <StatCard icon={Users} label="Visitantes únicos" value={stats.unique_visitors} accent="purple" />
            <StatCard icon={Globe} label="Visitas con sesión" value={stats.logged_in_visits} accent="emerald" />
          </div>
        )}

        {stats && (
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-bold text-[#0e4a7a] mb-3 flex items-center gap-2"><MapPin className="w-4 h-4" /> Por país</h3>
              <div className="space-y-2">
                {stats.by_country.length === 0 ? <p className="text-sm text-gray-400">Sin datos.</p> :
                  stats.by_country.map((r) => <BarRow key={r.label} {...r} max={max(stats.by_country)} />)}
              </div>
            </div>
            <div>
              <h3 className="font-bold text-[#0e4a7a] mb-3 flex items-center gap-2"><Smartphone className="w-4 h-4" /> Por dispositivo</h3>
              <div className="space-y-2">
                {stats.by_device.map((r) => <BarRow key={r.label} {...r} max={max(stats.by_device)} />)}
              </div>
              <h3 className="font-bold text-[#0e4a7a] mt-5 mb-3 flex items-center gap-2"><Laptop className="w-4 h-4" /> Por navegador</h3>
              <div className="space-y-2">
                {stats.by_browser.map((r) => <BarRow key={r.label} {...r} max={max(stats.by_browser)} />)}
              </div>
            </div>
          </div>
        )}

        {stats && stats.top_paths.length > 0 && (
          <div className="mt-6">
            <h3 className="font-bold text-[#0e4a7a] mb-3">Páginas más visitadas</h3>
            <div className="space-y-2">
              {stats.top_paths.map((r) => <BarRow key={r.label} {...r} max={max(stats.top_paths)} />)}
            </div>
          </div>
        )}
      </div>

      {/* Live list */}
      <div className="bg-white rounded-3xl shadow-md border border-blue-100 p-6 sm:p-8">
        <h3 className="text-lg font-extrabold text-[#0e4a7a] mb-4">Últimas visitas (200)</h3>
        <div className="flex items-center gap-2 flex-wrap mb-4">
          <input
            placeholder="País"
            value={filter.country}
            onChange={(e) => setFilter({ ...filter, country: e.target.value })}
            className="px-3 py-1.5 rounded-full border-2 border-blue-100 text-sm w-40"
          />
          <select
            value={filter.device}
            onChange={(e) => setFilter({ ...filter, device: e.target.value })}
            className="px-3 py-1.5 rounded-full border-2 border-blue-100 text-sm"
          >
            <option value="">Cualquier dispositivo</option>
            <option value="mobile">Mobile</option>
            <option value="desktop">Desktop</option>
            <option value="tablet">Tablet</option>
          </select>
          <input
            placeholder="Ruta (ej: /tauro)"
            value={filter.path}
            onChange={(e) => setFilter({ ...filter, path: e.target.value })}
            className="px-3 py-1.5 rounded-full border-2 border-blue-100 text-sm w-40"
          />
          <label className="text-xs flex items-center gap-1 text-gray-700">
            <input
              type="checkbox"
              checked={filter.only_logged_in}
              onChange={(e) => setFilter({ ...filter, only_logged_in: e.target.checked })}
            /> Solo registrados
          </label>
          <button onClick={load} className="px-3 py-1.5 rounded-full bg-[#1d6cb1] text-white font-semibold text-xs">Aplicar</button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-left uppercase tracking-wider text-gray-500 border-b border-blue-100">
              <tr>
                <th className="py-2 pr-2">Cuándo</th>
                <th className="py-2 pr-2">Ruta</th>
                <th className="py-2 pr-2">Visitante</th>
                <th className="py-2 pr-2">Dónde</th>
                <th className="py-2 pr-2">Dispositivo</th>
                <th className="py-2 pr-2">IP</th>
              </tr>
            </thead>
            <tbody>
              {list.map((v) => {
                const Icon = DEVICE_ICON[v.device] || Globe;
                return (
                  <tr key={v.id} className="border-b border-blue-50 hover:bg-blue-50/30">
                    <td className="py-2 pr-2 text-gray-500">{(v.created_at || "").slice(0, 19).replace("T", " ")}</td>
                    <td className="py-2 pr-2 font-mono text-[#0e4a7a]">{v.path}</td>
                    <td className="py-2 pr-2">{v.user_email ? <span className="text-emerald-700 font-bold">{v.user_email}</span> : <span className="text-gray-400">anónimo</span>}</td>
                    <td className="py-2 pr-2">{v.city ? `${v.city}, ` : ""}{v.country || "—"}</td>
                    <td className="py-2 pr-2">
                      <span className="inline-flex items-center gap-1">
                        <Icon className="w-3.5 h-3.5 text-[#1d6cb1]" /> {v.device} · {v.os} · {v.browser}
                      </span>
                    </td>
                    <td className="py-2 pr-2 font-mono text-gray-500">{v.ip}</td>
                  </tr>
                );
              })}
              {list.length === 0 && (
                <tr><td colSpan={6} className="py-6 text-center text-gray-500">Sin visitas para este filtro.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
